import Phaser from 'phaser';
import type { BaseUpgrade } from '@roe/shared';
import { setupCamera } from '../dpr.js';
import { GameStateManager } from '../state/GameStateManager';

export class BaseScene extends Phaser.Scene {
  private gsm!: GameStateManager;
  private battleScene!: Phaser.Scene & { resumeBattle(): void };
  private contentGroup!: Phaser.GameObjects.Group;
  private scrollOffset = 0;
  private totalContentHeight = 0;
  private readonly VIEWPORT_TOP = 82;
  private readonly VIEWPORT_HEIGHT = 470;

  constructor() {
    super({ key: 'BaseScene' });
  }

  init(data: { gsm: GameStateManager; battleScene: any }): void {
    this.gsm = data.gsm;
    this.battleScene = data.battleScene;
  }

  create(): void {
    setupCamera(this, this.cameras.main, 'overlay');
    this.scrollOffset = 0;
    this.cameras.main.setBackgroundColor('rgba(15, 15, 35, 0.94)');
    this.contentGroup = this.add.group();

    const W = 480;

    // Header
    this.add.rectangle(W / 2, 22, W, 44, 0x111827, 0.95).setDepth(0);
    this.add.text(W / 2, 20, 'BASE UPGRADES', {
      fontSize: '18px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(1);

    // Close button
    this.add.text(W - 20, 20, 'X', {
      fontSize: '18px', fontFamily: 'Georgia, serif', color: '#ef4444',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).on('pointerdown', () => this.closeScene());

    // Resources display
    this.add.text(10, 50, '', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#9ca3af',
    }).setName('resources');

    // Base stats
    this.add.text(10, 66, '', {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6b7280',
    }).setName('baseStats');

    // Scroll buttons
    this.add.text(W - 20, 280, '▼', {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: '#9ca3af',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.scroll(80));

    this.add.text(W - 20, 254, '▲', {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: '#9ca3af',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.scroll(-80));

    // Mouse wheel
    this.input.on('wheel', (_ptr: unknown, _gos: unknown, _dx: unknown, dy: number) => {
      this.scroll(dy > 0 ? 60 : -60);
    });

    this.refreshContent();
  }

  private scroll(delta: number): void {
    const maxScroll = Math.max(0, this.totalContentHeight - this.VIEWPORT_HEIGHT);
    this.scrollOffset = Math.max(0, Math.min(maxScroll, this.scrollOffset + delta));
    this.refreshContent();
  }

  private refreshContent(): void {
    this.contentGroup.clear(true, true);
    const p = this.gsm.player;

    // Update resource display
    const resText = this.children.getByName('resources') as Phaser.GameObjects.Text;
    if (resText) {
      resText.setText(`🪙 ${p.resources.gold}  ◆ ${p.resources.loot}`);
    }

    const statsText = this.children.getByName('baseStats') as Phaser.GameObjects.Text;
    if (statsText) {
      const maxSlots = this.gsm.getMaxDeploySlots();
      statsText.setText(`Hero Slots: ${p.deployedHeroes.length}/${maxSlots} (unlock at Lv ${(maxSlots) * 5})`);
    }

    // Categories
    const categories = ['fortification', 'barracks', 'armory', 'resource', 'research'] as const;
    const catNames: Record<string, string> = {
      fortification: 'Fortifications', barracks: 'Barracks',
      armory: 'Armory', resource: 'Resources', research: 'Research',
    };
    const catColors: Record<string, string> = {
      fortification: '#ef4444', barracks: '#3b82f6',
      armory: '#f97316', resource: '#fbbf24', research: '#8b2020',
    };

    // Scrollable vertical layout — two cards per row
    const cardW = 218;
    const cardH = 72;
    const gapX = 10;
    const gapY = 6;
    const startX = 10;
    let yOffset = this.VIEWPORT_TOP - this.scrollOffset;

    for (const category of categories) {
      const upgrades = p.base.upgrades.filter((u) => u.category === category);
      if (upgrades.length === 0) continue;

      // Category header
      const header = this.add.text(startX, yOffset, catNames[category], {
        fontSize: '12px', fontFamily: 'Georgia, serif', color: catColors[category], fontStyle: 'bold',
      });
      this.contentGroup.add(header);
      yOffset += 20;

      // Lay out upgrade cards 2-per-row
      for (let i = 0; i < upgrades.length; i += 2) {
        const x1 = startX;
        const x2 = startX + cardW + gapX;

        this.drawUpgradeCard(upgrades[i], x1, yOffset, cardW, cardH);
        if (i + 1 < upgrades.length) {
          this.drawUpgradeCard(upgrades[i + 1], x2, yOffset, cardW, cardH);
        }
        yOffset += cardH + gapY;
      }

      yOffset += 6;
    }

    this.totalContentHeight = yOffset - this.VIEWPORT_TOP + this.scrollOffset;
  }

  private drawUpgradeCard(upgrade: BaseUpgrade, x: number, y: number, w: number, h: number): void {
    const cost = this.gsm.getUpgradeCost(upgrade);
    const canAfford = this.gsm.canAffordUpgrade(upgrade);
    const isMaxed = upgrade.currentLevel >= upgrade.maxLevel;

    // Card background
    const bg = this.add.rectangle(x + w / 2, y + h / 2, w, h, 0x1e293b, 0.85);
    bg.setStrokeStyle(1, canAfford && !isMaxed ? 0xb07820 : 0x374151);
    this.contentGroup.add(bg);

    // Name and level
    const name = this.add.text(x + 8, y + 6, upgrade.name, {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#e2e8f0', fontStyle: 'bold',
    });
    this.contentGroup.add(name);

    const level = this.add.text(x + w - 8, y + 6, `Lv${upgrade.currentLevel}/${upgrade.maxLevel}`, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#9ca3af',
    }).setOrigin(1, 0);
    this.contentGroup.add(level);

    // Description
    const desc = this.add.text(x + 8, y + 22, upgrade.description, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6b7280',
      wordWrap: { width: w - 16 },
    });
    this.contentGroup.add(desc);

    if (isMaxed) {
      const maxed = this.add.text(x + w / 2, y + h - 14, 'MAX LEVEL', {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#22c55e', fontStyle: 'bold',
      }).setOrigin(0.5);
      this.contentGroup.add(maxed);
    } else {
      // Cost
      const costText = this.add.text(x + 8, y + h - 20, `🪙${cost.gold}  ◆${cost.loot}`, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: canAfford ? '#fbbf24' : '#4b5563',
      });
      this.contentGroup.add(costText);

      // Upgrade button
      const btn = this.add.text(x + w - 8, y + h - 20, 'UPGRADE', {
        fontSize: '10px', fontFamily: 'Georgia, serif',
        color: canAfford ? '#ffffff' : '#4b5563',
        backgroundColor: canAfford ? '#7a4010' : '#1f2937',
        padding: { x: 8, y: 3 },
      }).setOrigin(1, 0).setInteractive({ useHandCursor: canAfford });
      this.contentGroup.add(btn);

      if (canAfford) {
        btn.on('pointerover', () => btn.setStyle({ backgroundColor: '#9a5820' }));
        btn.on('pointerout', () => btn.setStyle({ backgroundColor: '#7a4010' }));
        btn.on('pointerdown', () => {
          this.gsm.purchaseUpgrade(upgrade.id);
          if (upgrade.effect === 'hero_slots') {
            this.showHeroSlotPrompt();
          }
          this.refreshContent();
        });
      }
    }
  }

  private showHeroSlotPrompt(): void {
    const W = 480;
    const H = 576;
    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.5).setDepth(200).setInteractive();
    const bg = this.add.rectangle(W / 2, H / 2, 300, 110, 0x1e293b, 0.98).setStrokeStyle(2, 0xb07820).setDepth(201);
    const msg = this.add.text(W / 2, H / 2 - 20, 'New hero slot unlocked!\nWant to buy a hero for it?', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#e2e8f0', align: 'center',
    }).setOrigin(0.5).setDepth(202);
    const yesBtn = this.add.text(W / 2 - 50, H / 2 + 22, 'SHOP', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#ffffff',
      backgroundColor: '#15803d', padding: { x: 12, y: 5 },
    }).setOrigin(0.5).setDepth(202).setInteractive({ useHandCursor: true });
    const noBtn = this.add.text(W / 2 + 50, H / 2 + 22, 'LATER', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#ffffff',
      backgroundColor: '#374151', padding: { x: 12, y: 5 },
    }).setOrigin(0.5).setDepth(202).setInteractive({ useHandCursor: true });
    const cleanup = () => { overlay.destroy(); bg.destroy(); msg.destroy(); yesBtn.destroy(); noBtn.destroy(); };
    yesBtn.on('pointerdown', () => {
      cleanup();
      this.gsm.save();
      this.battleScene.resumeBattle();
      this.scene.stop();
      this.scene.launch('ShopScene', { gsm: this.gsm, battleScene: this.battleScene });
    });
    noBtn.on('pointerdown', () => cleanup());
  }

  private closeScene(): void {
    this.gsm.save();
    this.battleScene.resumeBattle();
    this.scene.stop();
  }
}
